import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis,
} from "recharts";
import { Sparkle, CheckCircle, XCircle, Minus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import SearchSortBar from "@/components/SearchSortBar";
import useSearchSort from "@/hooks/useSearchSort";

function RecommendationBadge({ rec }) {
  const map = {
    sign: { cls: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40", icon: CheckCircle },
    negotiate: { cls: "bg-amber-500/20 text-amber-400 border-amber-500/40", icon: Minus },
    reject: { cls: "bg-rose-500/20 text-rose-400 border-rose-500/40", icon: XCircle },
  };
  const c = map[rec] || map.negotiate;
  return (
    <span className={`${c.cls} inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full border uppercase tracking-wider`}>
      <c.icon size={14} weight="fill" />
      {rec}
    </span>
  );
}

function DomainGauge({ d, delay = 0 }) {
  const statusColor = d.status === "positive" ? "#10B981" : d.status === "negative" ? "#F43F5E" : "#FBBF24";
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
    >
      <GlassCard className="p-5 h-full" hover>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="overline">{d.name}</div>
          </div>
          <span className={`chip chip-${d.status === "positive" ? "low" : d.status === "negative" ? "high" : "medium"}`}>{d.status}</span>
        </div>
        <div className="relative h-28 flex items-center justify-center">
          <ResponsiveContainer>
            <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" data={[{ value: d.score, fill: statusColor }]} startAngle={90} endAngle={-270}>
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar background={{ fill: "var(--surface-2)" }} dataKey="value" cornerRadius={20} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-2xl font-mono font-semibold" style={{ color: statusColor }}>{d.score}</div>
          </div>
        </div>
        <div className="text-xs text-[var(--text-2)] leading-relaxed mt-2">{d.summary}</div>
      </GlassCard>
    </motion.div>
  );
}

export default function BusinessImpact() {
  const { id } = useParams();
  const nav = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [current, setCurrent] = useState(id || null);
  const [bir, setBir] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/contracts").then(({ data }) => {
      setContracts(data);
      if (!current && data.length) setCurrent(data[0].id);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!current) return;
    setBir(null);
    api.get(`/bir/${current}`).then(({ data }) => setBir(data)).catch(() => setBir(null));
  }, [current]);

  const generate = async () => {
    setLoading(true);
    toast.info("Gemini is building your 8-domain Business Impact Report…");
    try {
      const { data } = await api.post(`/bir/${current}`);
      setBir(data);
      toast.success("BIR generated");
    } catch { toast.error("BIR failed"); }
    finally { setLoading(false); }
  };

  const activeContract = contracts.find((c) => c.id === current);
  const s = useSearchSort(contracts, {
    searchKeys: ["title", "counterparty", "tags"],
    sortOptions: [
      { value: "created_at", label: "Newest", defaultDir: "desc" },
      { value: "title", label: "Name A→Z", defaultDir: "asc" },
      { value: "value_amount", label: "Value", defaultDir: "desc" },
      { value: "risk_level", label: "Risk", defaultDir: "asc" },
    ],
    defaultSort: "created_at",
  });

  return (
    <PageShell title="Business Impact Report" subtitle="Pre-signature assessment across 8 business domains">
      <SearchSortBar controller={s} placeholder="Search contracts to run a BIR on…" testIdPrefix="bir" />
      <GlassCard className="p-4 mb-6 flex flex-wrap gap-2">
        {s.items.map((c) => (
          <button
            key={c.id}
            onClick={() => { setCurrent(c.id); nav(`/app/impact/${c.id}`, { replace: true }); }}
            data-testid={`bir-contract-${c.id}`}
            className={`px-3 py-1.5 rounded-md text-sm border transition ${
              current === c.id
                ? "bg-[var(--surface-strong)] border-[var(--border-strong)] text-[var(--text)]"
                : "bg-[var(--surface)] border-[var(--border)] text-[var(--text-2)] hover:text-[var(--text)]"
            }`}
          >
            {c.title}
          </button>
        ))}
      </GlassCard>

      {!bir && (
        <GlassCard className="p-12 text-center">
          <Sparkle size={32} weight="duotone" className="mx-auto mb-4 text-[var(--accent)]" />
          <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Generate Business Impact Report</h3>
          <p className="text-sm text-[var(--text-2)] max-w-md mx-auto mb-6">
            Gemini will assess this contract across Financial, Operational, Legal, Strategic, Reputational, Technology, Talent, and Sustainability domains.
          </p>
          <Button onClick={generate} disabled={loading || !current} data-testid="generate-bir-btn" size="lg" className="gap-2">
            <Sparkle size={16} weight="fill" />
            {loading ? "Generating with Gemini…" : "Generate BIR"}
          </Button>
        </GlassCard>
      )}

      {bir && (
        <>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <GlassCard strong className="p-8 md:p-10 mb-6">
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div>
                  <div className="overline mb-2 flex items-center gap-2">
                    <Sparkle size={12} weight="fill" className="text-[var(--accent)]" /> Executive summary
                  </div>
                  <h2 className="text-3xl font-medium tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                    {activeContract?.title || "Business Impact"}
                  </h2>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <RecommendationBadge rec={bir.recommendation} />
                  <div className="flex items-baseline gap-2">
                    <span className="text-6xl font-semibold tracking-tighter" style={{ fontFamily: 'var(--font-heading)' }}>
                      {bir.overall_score}
                    </span>
                    <span className="text-xl text-[var(--text-3)] font-mono">/100</span>
                  </div>
                </div>
              </div>
              <p className="text-lg leading-relaxed text-[var(--text)]" style={{ fontFamily: 'var(--font-heading)', fontWeight: 400 }}>
                {bir.summary}
              </p>
            </GlassCard>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(bir.domains || []).map((d, i) => (
              <DomainGauge key={d.name || i} d={d} delay={i * 0.06} />
            ))}
          </div>

          <div className="mt-8 text-center">
            <Button onClick={generate} variant="outline" size="sm" disabled={loading} data-testid="regenerate-bir-btn" className="glass-subtle border-[var(--border-strong)]">
              {loading ? "Regenerating…" : "Regenerate with fresh AI"}
            </Button>
          </div>
        </>
      )}
    </PageShell>
  );
}
